<template>
  <section class="statistics-page">
    <div class="stat-grid">
      <div v-for="item in stats" :key="item.label" class="stat-card">
        <div class="stat-label">{{ item.label }}</div>
        <div class="stat-value">{{ item.value }}</div>
      </div>
    </div>

    <div class="panel statistics-section">
      <div class="statistics-head">
        <h3>扫码趋势</h3>
        <div class="statistics-actions">
          <el-radio-group v-model="trendQuery.unit" size="small" @change="changeTrendUnit">
            <el-radio-button label="DAY">按日</el-radio-button>
            <el-radio-button label="WEEK">按周</el-radio-button>
            <el-radio-button label="MONTH">按月</el-radio-button>
          </el-radio-group>
          <el-select v-model="trendQuery.periods" size="small" style="width: 120px" @change="loadTrend">
            <el-option v-for="item in periodOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </div>
      </div>
      <div ref="trendChart" class="statistics-chart"></div>
    </div>

    <div class="statistics-grid">
      <div class="panel statistics-section">
        <div class="statistics-head">
          <h3>{{ regionTitle }}</h3>
          <el-button
            v-if="regionLevel === 'CITY'"
            size="small"
            icon="el-icon-back"
            @click="backToProvince"
          >
            返回省份
          </el-button>
        </div>
        <div ref="regionChart" class="statistics-chart"></div>
      </div>

      <div class="panel statistics-section">
        <div class="statistics-head">
          <h3>分布明细</h3>
          <span class="muted">{{ regionLevel === 'CITY' ? selectedProvince : '按省份汇总' }}</span>
        </div>
        <el-table :data="region" border size="small" height="320" @row-click="openCityRegion">
          <el-table-column prop="region" label="地区" min-width="150" />
          <el-table-column prop="totalScan" label="扫码量" width="90" sortable />
          <el-table-column prop="successScan" label="首次正品" width="95" sortable />
          <el-table-column prop="repeatScan" label="重复" width="80" sortable />
          <el-table-column prop="abnormalScan" label="异常" width="80" sortable />
        </el-table>
      </div>
    </div>

    <div class="panel statistics-section">
      <div class="statistics-head">
        <h3>产品扫码排行</h3>
        <span class="muted">按扫码总量排序</span>
      </div>
      <el-table :data="productRank" border size="small">
        <el-table-column type="index" label="排名" width="70" />
        <el-table-column prop="productName" label="产品名称" min-width="180" />
        <el-table-column prop="productId" label="产品 ID" width="100" />
        <el-table-column prop="count" label="扫码量" width="120" sortable />
      </el-table>
    </div>
  </section>
</template>

<script>
import http from '../api'
import * as echarts from 'echarts'

const defaultPeriods = { DAY: 30, WEEK: 12, MONTH: 12 }

export default {
  data() {
    return {
      overview: {},
      trend: [],
      region: [],
      productRank: [],
      regionLevel: 'PROVINCE',
      selectedProvince: '',
      trendQuery: { unit: 'DAY', periods: defaultPeriods.DAY },
      trendChart: null,
      regionChart: null
    }
  },
  computed: {
    stats() {
      return [
        { label: '总扫码量', value: this.overview.totalScan || 0 },
        { label: '首次正品', value: this.overview.successScan || 0 },
        { label: '重复扫码', value: this.overview.repeatScan || 0 },
        { label: '异常扫码', value: this.overview.abnormalScan || 0 },
        { label: '防伪码总数', value: this.overview.totalCode || 0 },
        { label: '已核验码', value: this.overview.verifiedCode || 0 }
      ]
    },
    periodOptions() {
      const unitText = { DAY: '天', WEEK: '周', MONTH: '个月' }[this.trendQuery.unit]
      const values = this.trendQuery.unit === 'DAY' ? [7, 15, 30, 60, 90] : [4, 8, 12, 24, 36]
      return values.map(value => ({ label: `近 ${value} ${unitText}`, value }))
    },
    regionTitle() {
      return this.regionLevel === 'CITY' ? `${this.selectedProvince}扫码城市分布` : '扫码省份分布'
    }
  },
  mounted() {
    this.load()
    window.addEventListener('resize', this.resizeCharts)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts)
    if (this.trendChart) this.trendChart.dispose()
    if (this.regionChart) this.regionChart.dispose()
  },
  methods: {
    async load() {
      const [overview, productRank] = await Promise.all([
        http.get('/admin/statistics/overview'),
        http.get('/admin/statistics/product-rank', { params: { limit: 10 } })
      ])
      this.overview = overview
      this.productRank = productRank
      await Promise.all([this.loadTrend(), this.loadRegion()])
    },
    async loadTrend() {
      this.trend = await http.get('/admin/statistics/trend', { params: this.trendQuery })
      this.$nextTick(this.renderTrendChart)
    },
    async loadRegion() {
      const params = { level: this.regionLevel, limit: 50 }
      if (this.regionLevel === 'CITY') params.province = this.selectedProvince
      this.region = await http.get('/admin/statistics/region', { params })
      this.$nextTick(this.renderRegionChart)
    },
    changeTrendUnit() {
      this.trendQuery.periods = defaultPeriods[this.trendQuery.unit]
      this.loadTrend()
    },
    openCityRegion(row) {
      if (this.regionLevel === 'CITY' || !row || !row.province) return
      this.regionLevel = 'CITY'
      this.selectedProvince = row.province
      this.loadRegion()
    },
    backToProvince() {
      this.regionLevel = 'PROVINCE'
      this.selectedProvince = ''
      this.loadRegion()
    },
    renderTrendChart() {
      if (!this.$refs.trendChart) return
      if (!this.trendChart) this.trendChart = echarts.init(this.$refs.trendChart)
      const seriesConfig = [
        { name: '扫码总量', key: 'totalScan', color: '#2563eb' },
        { name: '首次正品', key: 'successScan', color: '#10b981' },
        { name: '重复扫码', key: 'repeatScan', color: '#f59e0b' },
        { name: '异常扫码', key: 'abnormalScan', color: '#ef4444' }
      ]
      this.trendChart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { top: 0, right: 0, itemWidth: 12, itemHeight: 8 },
        color: seriesConfig.map(item => item.color),
        grid: { left: 42, right: 20, top: 48, bottom: 34 },
        xAxis: { type: 'category', data: this.trend.map(item => item.date), boundaryGap: false },
        yAxis: { type: 'value', minInterval: 1 },
        series: seriesConfig.map(item => ({
          name: item.name,
          type: 'line',
          smooth: true,
          symbolSize: 7,
          lineStyle: { width: 3 },
          data: this.trend.map(row => row[item.key] || 0)
        }))
      })
    },
    renderRegionChart() {
      if (!this.$refs.regionChart) return
      if (!this.regionChart) this.regionChart = echarts.init(this.$refs.regionChart)
      this.regionChart.off('click')
      this.regionChart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        color: ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444'],
        legend: { top: 0, right: 0, itemWidth: 12, itemHeight: 8 },
        grid: { left: 48, right: 18, top: 48, bottom: 58 },
        xAxis: {
          type: 'category',
          data: this.region.map(item => item.region),
          axisLabel: { interval: 0, rotate: this.region.length > 8 ? 35 : 0 }
        },
        yAxis: { type: 'value', minInterval: 1 },
        series: [
          { name: '扫码量', type: 'bar', barMaxWidth: 28, data: this.region.map(item => ({ value: item.totalScan || 0, province: item.province })) },
          { name: '首次正品', type: 'bar', barMaxWidth: 28, data: this.region.map(item => item.successScan || 0) },
          { name: '重复', type: 'bar', barMaxWidth: 28, data: this.region.map(item => item.repeatScan || 0) },
          { name: '异常', type: 'bar', barMaxWidth: 28, data: this.region.map(item => item.abnormalScan || 0) }
        ]
      })
      if (this.regionLevel === 'PROVINCE') {
        this.regionChart.on('click', params => {
          const row = this.region[params.dataIndex]
          this.openCityRegion(row)
        })
      }
    },
    resizeCharts() {
      if (this.trendChart) this.trendChart.resize()
      if (this.regionChart) this.regionChart.resize()
    }
  }
}
</script>

<style scoped>
.statistics-page {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.statistics-section h3 {
  margin: 0;
  font-size: 16px;
  color: #0f3d34;
}

.statistics-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.statistics-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.statistics-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(420px, .9fr);
  gap: 18px;
}

.statistics-chart {
  height: 320px;
}

@media (max-width: 1100px) {
  .statistics-grid {
    grid-template-columns: 1fr;
  }
}
</style>
